import { Box, Typography } from '@mui/material';
import { cardSurfaceSx } from '../../theme';
import type { ListCardProps } from './types';

/**
 * A titled card holding a bulleted list. The four ICOM cards, "What You
 * Deliver", and anything else the content gives us as a list of sentences.
 *
 * **A card with no items renders nothing.** Between a third and a tenth of the
 * pages are missing any given one of these lists — 42 have no Controls +
 * Support — so absence is the normal state rather than a fault, and a heading
 * over an empty box would read as content that failed to load. The section
 * around it decides what to do when every card is empty.
 *
 * **The bullet is drawn here and is `aria-hidden`.** It is a literal `•` inside
 * the Figma text nodes, which makes it look like content; the API returns clean
 * strings. This is a real `ul`, so the list semantics are already carried by
 * the markup and a screen reader announcing "bullet" five times adds nothing.
 */
export const ListCard = ({ heading, caption, items, level = 3 }: ListCardProps) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        ...cardSurfaceSx,
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        // Fills the row it is in, so two cards side by side match rather than
        // stepping down to their own content heights.
        height: '100%',
      }}
    >
      <Box>
        <Typography variant="h3" component={`h${String(level)}` as 'h2' | 'h3'}>
          {heading}
        </Typography>

        {caption !== undefined && (
          <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 1 }}>
            {caption}
          </Typography>
        )}
      </Box>

      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {items.map((item) => (
          <Box
            key={item}
            component="li"
            sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
          >
            <Typography
              component="span"
              variant="body2"
              aria-hidden
              sx={{ color: 'text.secondary', flexShrink: 0 }}
            >
              &bull;
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
